import {
	dateFormats,
	DateOfBirthSchema,
	StringDateApiFormatType,
	useIntl,
	type UserItalianOAMRequirementConfig,
	type UserItalianOAMUserProvidedValues,
	type UserRequirement
} from '@capsicum/common';
import { useMe } from '@capsicum/socios-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ITALIAN_COUNTRY } from '../constants/regulations';
import { useSubmitRequirementStateByIdentifierQuery } from '../data-access';

type ItalianOamFormType = z.infer<ReturnType<typeof useItalianOamSchema>>;

type ItalianOAMProps = {
	requirement: UserRequirement;
};

export type ItalianOAMCountryProps = {
	name?: string;
};

export type ItalianOAMMunicipalityProps = {
	name?: string;
};

export type ItalianOAMProvinceProps = {
	municipalities?: Array<ItalianOAMMunicipalityProps>;
	name?: string;
};

const useItalianOamSchema = () => {
	const { $t } = useIntl();

	const MunicipalitySchema = z.object({
		name: z.string()
	});

	const ProvinceSchema = z.object({
		municipalities: z.array(MunicipalitySchema).optional(),
		name: z.string()
	});

	return z.object({
		addressCountry: z.object({
			name: z.string()
		}),
		addressMunicipality: z.union([
			z
				.string()
				.min(
					2,
					$t('requirements.common.form.errors.length', {
						field: $t('jurisdictionRequirements.italianOAM.form.residenceMunicipality.label'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.short')
					})
				)
				.max(
					255,
					$t('requirements.common.form.errors.length', {
						field: $t('jurisdictionRequirements.italianOAM.form.residenceMunicipality.label'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.long')
					})
				),
			MunicipalitySchema
		]),
		addressProvince: ProvinceSchema.nullable(),
		addressStreet: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.street.label'),
					max: 512,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				512,
				$t('requirements.common.form.errors.length', {
					field: $t('requirements.common.form.street.label'),
					max: 512,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		birthCountry: z.object({
			name: z.string()
		}),
		birthMunicipality: z.union([
			z
				.string()
				.min(
					2,
					$t('requirements.common.form.errors.length', {
						field: $t('requirements.common.form.city.label'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.short')
					})
				)
				.max(
					255,
					$t('requirements.common.form.errors.length', {
						field: $t('requirements.common.form.city.label'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.long')
					})
				),
			MunicipalitySchema
		]),
		codiceFiscale: z.string().refine(value => value.length === 16, {
			message: $t('jurisdictionRequirements.italianOAM.form.codiceFiscale.error')
		}),
		dateOfBirth: DateOfBirthSchema({
			$t,
			checkAgeOfMajority: true
		}),
		firstName: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.firstName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.firstName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		lastName: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.lastName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.lastName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			)
	});
};

const useItalianOamForm = (config: UserItalianOAMRequirementConfig) => {
	const { birthDate: dateOfBirth, firstName, lastName } = config.prefill.user || {};

	const codiceFiscale = config.prefill.codiceFiscale;

	const { city, region, street } = config.prefill.address || {};

	const provinces = config.provinces as Array<ItalianOAMProvinceProps>;
	const findProvinceByRegion = (region: string | undefined) => {
		return provinces?.find(province => province.name && province.name.toLowerCase() === region?.toLowerCase());
	};
	const province = findProvinceByRegion(region);

	const findMunicipality = (addressCity: string | undefined): ItalianOAMMunicipalityProps | undefined => {
		if (province === undefined || addressCity === undefined || addressCity === null) {
			return undefined;
		}

		const foundCity = province.municipalities?.find(
			municipality => municipality.name?.toLowerCase() === addressCity.toLowerCase()
		);

		if (foundCity) {
			return foundCity;
		}

		return undefined;
	};

	const municipality = findMunicipality(city);

	const defaultValues = useMemo(
		() => ({
			addressCountry: { name: ITALIAN_COUNTRY },
			addressMunicipality: municipality,
			addressProvince: province,
			addressStreet: street,
			birthCountry: { name: ITALIAN_COUNTRY },
			birthMunicipality: '',
			codiceFiscale: codiceFiscale || '',
			dateOfBirth: dateOfBirth,
			firstName: firstName || '',
			lastName: lastName || ''
		}),
		[firstName, lastName, codiceFiscale, dateOfBirth, province, municipality, street]
	);

	const italianOamForm = useForm<ItalianOamFormType>({
		defaultValues,
		mode: 'onChange',
		resolver: zodResolver(useItalianOamSchema())
	});

	return italianOamForm;
};

export const useItalianOam = ({ requirement }: ItalianOAMProps) => {
	const { me } = useMe();
	const canUpdatePersonalData = !me?.metadata?.hasVerifiedName;
	const config = requirement.config as UserItalianOAMRequirementConfig;
	const translatedCountries = config.countries;

	const italianOamForm = useItalianOamForm(config);
	const addressCountry = italianOamForm.watch('addressCountry');

	const provinces = config.provinces;
	const addressProvince = italianOamForm.watch('addressProvince');
	const defaultProvince = italianOamForm.watch('addressProvince')?.name;

	const birthCountry = italianOamForm.watch('birthCountry');
	const [selectedBirthProvince, setSelectedBirthProvince] = useState<ItalianOAMProvinceProps | undefined>(undefined);
	const selectBirthProvince = (value: ItalianOAMProvinceProps) => {
		setSelectedBirthProvince(value);
		italianOamForm.resetField('birthMunicipality');
	};
	const resetBirthInformation = () => {
		setSelectedBirthProvince(undefined);
		italianOamForm.resetField('birthMunicipality');
	};

	const resetResidenceMunicipality = () => {
		italianOamForm.setValue('addressMunicipality', '');
		italianOamForm.clearErrors('addressMunicipality');
	};

	const resetResidenceInformation = () => {
		italianOamForm.setValue('addressProvince', null);
		resetResidenceMunicipality();
	};

	const filterMunicipalitiesByProvince = useCallback(
		(provinceName: string) => {
			const selectedAddressProvince = provinces?.find(
				(province: ItalianOAMProvinceProps) => province.name === provinceName
			);

			return selectedAddressProvince?.municipalities || [];
		},
		[provinces]
	);

	const {
		error: submitRequirementError,
		isLoading,
		isSuccess,
		mutate
	} = useSubmitRequirementStateByIdentifierQuery({ enableFormErrorInterceptor: true });

	const handleSubmitForm = italianOamForm.handleSubmit(data => {
		const birthDate = dateFormats.format(data.dateOfBirth, StringDateApiFormatType);

		const payload: UserItalianOAMUserProvidedValues = {
			addressCountry: data.addressCountry.name,
			addressMunicipality:
				typeof data.addressMunicipality === 'string'
					? data.addressMunicipality
					: data.addressMunicipality?.name,
			addressProvince: data.addressProvince?.name,
			addressStreet: data.addressStreet,
			birthCountry: data.birthCountry.name,
			birthMunicipality:
				typeof data.birthMunicipality === 'string' ? data.birthMunicipality : data.birthMunicipality?.name,
			codiceFiscale: data.codiceFiscale,
			dateOfBirth: birthDate,
			firstName: data.firstName,
			lastName: data.lastName
		};

		mutate({
			requirementData: payload,
			requirementIdentifier: requirement.identifier
		});
	});

	return {
		addressCountry,
		addressProvince,
		birthCountry,
		canUpdatePersonalData,
		defaultProvince,
		filterMunicipalitiesByProvince,
		handleSubmitForm,
		isLoading: isLoading || isSuccess,
		italianOamForm,
		provinces,
		resetBirthInformation,
		resetResidenceInformation,
		resetResidenceMunicipality,
		selectBirthProvince,
		selectedBirthProvince,
		submitRequirementError,
		translatedCountries
	};
};
